import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { countBy, keyBy } from 'lodash-es';
import { Spinner, TableList } from 'seagull';
import { useFetchAgreementStudioData } from "../../../components/AgreementStudio/useFetchAgreementStudioData";
import GenericError from "../../../components/GenericError/GenericError";
import { useIoContext, useStoreActions } from "../../../contexts";
import { useContacts } from "../../../hooks/reactQuery/queries/useContacts";
import { useOnMount } from "../../../hooks/useOnMount";
import { useRestfulWrapper } from "../../../hooks/useRestfulWrapper";
import { useStoreStatic } from "../../../store/store-context";
import { editDraftsTableHeaderColumnDefs, filterDrafts, getLegalTerms as _getLegalTerms, sortDrafts } from "./EditDraftsTable.utils";
import EditDraftsTableRow from "./EditDraftsTableRow";
import { ColumnSortId } from "./types";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export function EditDraftsTableWithData(props) {
  const {
    visibleColumns,
    search,
    selected,
    setSelected
  } = props;
  const {
    getState
  } = useStoreStatic();
  const {
    getNow
  } = useIoContext();
  const [drafts, setDrafts] = useState(props.drafts);
  useEffect(() => setDrafts(props.drafts), [props.drafts]);
  const [sort, setSort] = useState({
    sortId: ColumnSortId.LastModified,
    sort: 'desc'
  });
  const {
    contacts
  } = useContacts();
  const headerItems = editDraftsTableHeaderColumnDefs(visibleColumns);
  const contactMap = useMemo(() => keyBy(contacts || [], c => c.id()), [contacts]);
  const getContact = useCallback(draft => {
    const clientContactId = draft.clientContactId();
    return clientContactId ? contactMap[clientContactId] || null : null;
  }, [contactMap]);
  const sortedAndFilteredDrafts = useMemo(() => {
    var _getState$userInfo;
    const filteredDrafts = filterDrafts(drafts, getContact, search);
    const sortedDrafts = sortDrafts({
      drafts: filteredDrafts,
      sortId: sort.sortId,
      sort: sort.sort,
      introductoryMessages: (_getState$userInfo = getState().userInfo) === null || _getState$userInfo === void 0 || (_getState$userInfo = _getState$userInfo.businessSettings()) === null || _getState$userInfo === void 0 ? void 0 : _getState$userInfo.introductoryMessages,
      getContact,
      getLegalTerms: draft => _getLegalTerms(draft, getState().legalTerms.data)
    });
    return sortedDrafts;
  }, [drafts, search, sort, getContact]);
  function updateDraft(updatedDraft) {
    const withLastModifiedOn = updatedDraft.setLastModifiedOn(getNow().getTime());
    setDrafts(drafts => drafts.map(d => d.id() === updatedDraft.id() ? withLastModifiedOn : d));
  }
  const deleteDraft = draft => {
    setDrafts(drafts => drafts.filter(d => d.id() !== draft.id()));
  };
  const onToggleAllSelection = useCallback(selectAll => setSelected(drafts.reduce((acc, d) => Object.assign(acc, {
    [d.id()]: selectAll
  }), {})), [drafts]);
  const numSelected = useMemo(() => {
    var _countBy$true;
    return (_countBy$true = countBy(selected).true) !== null && _countBy$true !== void 0 ? _countBy$true : 0;
  }, [selected]);
  return /*#__PURE__*/_jsx("div", {
    "data-testid": "edit-drafts-table",
    children: /*#__PURE__*/_jsx(TableList.Provider, {
      hasActionColumn: true,
      columnDefs: headerItems,
      expandableRows: true,
      expandableRowsVariant: "clickableIcon",
      enableRowSelection: true,
      selectedRows: selected,
      onSelectionChange: setSelected,
      numRows: drafts.length,
      numSelectedRows: numSelected,
      onToggleAllSelection: onToggleAllSelection,
      sort: {
        sort: sort.sort,
        colIndex: headerItems.findIndex(item => item.sortId === sort.sortId)
      },
      onSortChange: tableListSort => {
        if (!tableListSort) {
          setSort({
            sortId: ColumnSortId.PrimaryContact,
            sort: 'asc'
          });
        } else {
          setSort({
            sortId: headerItems[tableListSort.colIndex].sortId,
            sort: tableListSort.sort
          });
        }
      },
      children: /*#__PURE__*/_jsx(TableList.Structure, {
        header: /*#__PURE__*/_jsx(TableList.Header, {
          stickyToTop: true,
          items: headerItems,
          "data-testid": "edit-drafts-table-header"
        }),
        content: /*#__PURE__*/_jsx(_Fragment, {
          children: sortedAndFilteredDrafts.map((draft, index) => /*#__PURE__*/_jsx(EditDraftsTableRow, {
            draft: draft,
            index: index,
            visibleColumns: visibleColumns,
            getContact: getContact,
            updateDraft: updateDraft,
            deleteDraft: deleteDraft
          }, draft.id()))
        })
      })
    })
  });
}
const EditDraftsTable = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    visibleColumns,
    search,
    selected,
    setSelected
  } = _ref;
  const {
    api
  } = useStoreActions();
  const fetchDrafts = () => api.draft.getFullDrafts({
    limit: 1000,
    page: 1
  });
  const {
    refresh,
    renderRestfulState
  } = useRestfulWrapper({
    fetch: fetchDrafts
  });
  const fetchAgreementStudioData = useFetchAgreementStudioData({
    disableLoader: true,
    disableAgreements: true,
    disableProposals: true
  });
  useOnMount(() => {
    fetchAgreementStudioData();
  });
  useImperativeHandle(ref, () => ({
    refresh
  }));
  return renderRestfulState({
    renderLoader: () => /*#__PURE__*/_jsx(Spinner, {
      centeredInBox: true,
      centerBoxHeight: "100%",
      "data-testid": "edit-drafts-table-spinner"
    }),
    renderError: () => /*#__PURE__*/_jsx(GenericError, {
      testid: "edit-drafts-table-error"
    }),
    renderChild: _ref2 => {
      let {
        data
      } = _ref2;
      return /*#__PURE__*/_jsx(EditDraftsTableWithData, {
        drafts: data,
        visibleColumns: visibleColumns,
        search: search,
        selected: selected,
        setSelected: setSelected
      });
    }
  });
});
export default EditDraftsTable;